import { useCompanyEnrichment } from "@/hooks/useCompanyEnrichment";
import { CircleCheckIcon, Loader2Icon, SparklesIcon } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";

type Props = {
  t: (key: string) => string;
  enrichmentLevel: number;
  companyId: string;
};

/**
 * Row-level indicator reflecting a company's enrichment state. Shows a spinner
 * while an enrichment asked from the side panel runs, a sparkle while the company
 * is not yet fully enriched, and a check once enrichment is COMPLETE.
 */
export function CompanyEnrichingIndicator({
  t,
  enrichmentLevel,
  companyId,
}: Props): React.ReactElement {
  const { isEnriching, latestData } = useCompanyEnrichment(companyId);

  const effectiveLevel = latestData?.enrichmentLevel ?? enrichmentLevel;
  const isComplete = effectiveLevel >= 4;
  const showSpinner = isEnriching && !isComplete;

  let icon: React.ReactNode;
  let tooltipKey: string;
  let iconColorClass: string;

  if (showSpinner) {
    icon = <Loader2Icon className="w-4 h-4 animate-spin" />;
    tooltipKey = "components.dataTableCompanies.columns.enrichingTooltip";
    iconColorClass = "text-muted-foreground";
  } else if (isComplete) {
    icon = <CircleCheckIcon className="w-4 h-4" />;
    tooltipKey = "components.dataTableCompanies.columns.enrichmentCompleteTooltip";
    iconColorClass = "text-success";
  } else {
    icon = <SparklesIcon className="w-4 h-4" />;
    tooltipKey = "components.dataTableCompanies.columns.enrichmentPendingTooltip";
    iconColorClass = "text-muted-foreground";
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={`inline-flex items-center justify-center cursor-help ${iconColorClass}`}
          >
            {icon}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          {t(tooltipKey)}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
